import { useCallback, useMemo, useState } from 'react'

import { MultiSelect, Select } from 'govwell-ui'
import { SelectBaseProps, SelectModeProps, SelectOption } from 'govwell-ui/components/Select/types'
import { observer } from 'mobx-react-lite'
import { UserDataFragment, UserType } from 'types/graphql'

import useDebounce from 'src/hooks/use-debounce'
import {
  compareUsersByLabel,
  filterNil,
  formatUserLabel,
  sortUsersByGroupThenByLabel,
} from 'src/utils'

import { useSearchUsersQuery } from '../../fetch/users'

export type UserSearchGroup = {
  label: string
  sortRank: number
  users: UserDataFragment[]
}

type Props = SelectModeProps<UserDataFragment> & {
  caption?: React.ReactNode
  customDisplay?: (user: UserDataFragment) => React.ReactNode
  errorMessage?: string
  getIsOptionDisabled?: (option: SelectOption<UserDataFragment>) => boolean
  includeUsersFromAllOrganizations?: boolean
  isClearable?: boolean
  isInvalid?: boolean
  isRequired?: boolean
  label?: React.ReactNode
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  placeholder?: string
  recordUuids?: string[]
  type?: UserType
  userGroups?: UserSearchGroup[]
  width?: React.CSSProperties['width']
}

const UserSearch = ({
  caption,
  customDisplay,
  errorMessage,
  getIsOptionDisabled,
  includeUsersFromAllOrganizations,
  isClearable,
  isInvalid,
  isMultiSelect,
  isRequired,
  label,
  onBlur,
  onValueChange,
  placeholder,
  recordUuids,
  type,
  userGroups,
  value,
  width,
}: Props) => {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query)
  const { data, isFetching } = useSearchUsersQuery({
    query: debouncedQuery,
    input: {
      includeAllOrgs: includeUsersFromAllOrganizations,
      recordUuids,
      type,
    },
  })

  const handleSelectedOptionsChange = useCallback(
    (newSelectedOptions: SelectOption<UserDataFragment>[] | undefined) => {
      if (isMultiSelect) {
        onValueChange(newSelectedOptions?.map((o) => o.value) ?? [])
      }
    },
    [isMultiSelect, onValueChange]
  )

  const handleSelectedOptionChange = useCallback(
    (newSelectedOption: SelectOption<UserDataFragment> | undefined) => {
      if (!isMultiSelect) {
        onValueChange(newSelectedOption?.value)
      }
    },
    [isMultiSelect, onValueChange]
  )

  const valueAsArray = useMemo(() => (Array.isArray(value) ? value : filterNil([value])), [value])

  // Derive per-user lookup maps and pre-built initial options from the caller's
  // group definitions. These are used for two purposes:
  //   1. initialOptions — shown before the user types a search query
  //   2. labelMap / rankMap — used by sortUsersByGroupThenByLabel to order search results
  const { userIdToGroupLabel, userIdToGroupSortRank, initialOptions } = useMemo(() => {
    if (!userGroups) {
      return {
        userIdToGroupLabel: undefined,
        userIdToGroupSortRank: undefined,
        initialOptions: undefined,
      }
    }

    const labelMap = new Map<number, string>()
    const rankMap = new Map<number, number>()
    const opts: SelectOption<UserDataFragment>[] = []

    for (const group of userGroups) {
      const sorted = [...group.users].sort(compareUsersByLabel)
      for (const user of sorted) {
        labelMap.set(user.id, group.label)
        rankMap.set(user.id, group.sortRank)
        opts.push({ customDisplay, group: group.label, label: formatUserLabel(user), value: user })
      }
    }

    return { userIdToGroupLabel: labelMap, userIdToGroupSortRank: rankMap, initialOptions: opts }
  }, [customDisplay, userGroups])

  const options = useMemo(() => {
    const selectedUserIds = new Set<number>(valueAsArray.map((u) => u.id))

    const getGroupLabel = (user: UserDataFragment) =>
      userIdToGroupLabel?.get(user.id) ?? (userIdToGroupLabel ? 'Other' : undefined)
    const getGroupSortRank = (user: UserDataFragment) => userIdToGroupSortRank?.get(user.id)

    const toOption = (user: UserDataFragment, group?: string): SelectOption<UserDataFragment> => ({
      customDisplay,
      group,
      label: formatUserLabel(user),
      value: user,
    })
    const selected = valueAsArray.map((u) => toOption(u))

    if (initialOptions && !debouncedQuery) {
      return [...selected, ...initialOptions.filter((o) => !selectedUserIds.has(o.value.id))]
    }

    const searchResults = sortUsersByGroupThenByLabel({
      users: (data?.searchUsers ?? []).filter((user) => !selectedUserIds.has(user.id)),
      getGroupSortRank,
      getGroupLabel,
    })
    return [...selected, ...searchResults.map((user) => toOption(user, getGroupLabel(user)))]
  }, [
    customDisplay,
    data?.searchUsers,
    debouncedQuery,
    initialOptions,
    userIdToGroupLabel,
    userIdToGroupSortRank,
    valueAsArray,
  ])

  const selectedOptionSingleSelect = useMemo(() => {
    if (!isMultiSelect) {
      return options.find((o) => o.value === value)
    }
  }, [isMultiSelect, options, value])

  const selectedOptions = useMemo(
    () => options.filter((o) => valueAsArray.includes(o.value)),
    [options, valueAsArray]
  )

  const getOptionKey = useCallback((option: SelectOption<UserDataFragment>) => option.value.id, [])

  const commonProps: SelectBaseProps<UserDataFragment> = {
    ...(label ? { label } : { ariaLabel: 'User' }),
    caption,
    errorMessage,
    getIsOptionDisabled,
    getOptionKey,
    isAsync: true,
    isClearable,
    isInvalid,
    isLoading: isFetching,
    isRequired,
    width,
    onBlur,
    options,
    placeholder: placeholder ?? 'Search for users by name, company, email or phone',
    onSearchQueryChange: setQuery,
  }

  if (isMultiSelect) {
    return (
      <MultiSelect
        selectedOptions={selectedOptions}
        onSelectedOptionsChange={handleSelectedOptionsChange}
        {...commonProps}
      />
    )
  }

  return (
    <Select
      onSelectedOptionChange={handleSelectedOptionChange}
      selectedOption={selectedOptionSingleSelect}
      {...commonProps}
    />
  )
}

export default observer(UserSearch)
